import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { itIT } from '@mui/x-data-grid/locales';

export interface ColumnMappingField {
  field: string;
  headerName: string;
  width?: number;
  /** Se true, la colonna è calcolata: mostrata in rosso nel modale e non usata in import */
  calculated?: boolean;
}

export interface ColumnMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (mapping: Map<number, string>) => void;
  clipboardData: string[][];
  fields: ColumnMappingField[];
  dataOrientation?: 'column' | 'row'; // 'column' = dati per colonna (default), 'row' = dati per riga (ruotato)
}

export function ColumnMappingDialog({
  open,
  onClose,
  onImport,
  clipboardData,
  fields,
  dataOrientation = 'column',
}: ColumnMappingDialogProps) {
  const [columnMapping, setColumnMapping] = React.useState<Map<number, string>>(new Map());

  // Prevalorizza il mapping basato sull'ordine delle colonne (stesso ordine della tabella)
  React.useEffect(() => {
    if (open && clipboardData.length > 0) {
      const initialMapping = new Map<number, string>();
      const maxColumns = Math.max(...clipboardData.map(row => row.length));

      fields.forEach((field, index) => {
        if (index < maxColumns) {
          initialMapping.set(index, field.field);
        }
      });

      setColumnMapping(initialMapping);
    }
  }, [open, clipboardData, fields]);

  // Ruota i dati se dataOrientation è 'row'
  const processedData = React.useMemo(() => {
    if (dataOrientation === 'row') {
      // Ruota i dati: ogni colonna diventa una riga
      const maxCols = Math.max(...clipboardData.map(row => row.length));
      const rotated: string[][] = [];

      for (let colIdx = 0; colIdx < maxCols; colIdx++) {
        const newRow: string[] = [];
        for (let rowIdx = 0; rowIdx < clipboardData.length; rowIdx++) {
          newRow.push(clipboardData[rowIdx][colIdx] || '');
        }
        rotated.push(newRow);
      }

      return rotated;
    }
    return clipboardData;
  }, [clipboardData, dataOrientation]);

  const maxColumns = Math.max(...processedData.map(row => row.length));
  const headers = Array.from({ length: maxColumns }, (_, i) => `Colonna ${i + 1}`);

  const handleImport = () => {
    if (columnMapping.size === 0) {
      return;
    }
    onImport(columnMapping);
    setColumnMapping(new Map());
    onClose();
  };

  const handleClose = () => {
    setColumnMapping(new Map());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Mappa colonne da Excel</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ height: '60vh', width: '100%', mt: 2 }}>
          <DataGrid
            rows={processedData.map((row, idx) => ({
              id: idx,
              ...row.reduce(
                (acc, cell, colIdx) => {
                  acc[`col_${colIdx}`] = cell;
                  return acc;
                },
                {} as Record<string, string>
              ),
            }))}
            columns={headers.map((header, colIdx) => ({
              field: `col_${colIdx}`,
              headerName: '',
              width: 200,
              renderHeader: () => {
                const fieldOptions = [
                  { value: '', label: '-- Nessun mapping --', calculated: false },
                  ...fields.map(f => ({
                    value: f.field,
                    label: f.calculated ? `${f.headerName || f.field} (calcolato)` : (f.headerName || f.field),
                    calculated: f.calculated ?? false,
                  })),
                ];
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel shrink>{header}</InputLabel>
                      <Select
                        value={columnMapping.get(colIdx) || ''}
                        onChange={e => {
                          const newMapping = new Map(columnMapping);
                          if (e.target.value) {
                            newMapping.set(colIdx, e.target.value);
                          } else {
                            newMapping.delete(colIdx);
                          }
                          setColumnMapping(newMapping);
                        }}
                        displayEmpty
                        notched
                        label={header}
                        renderValue={value => {
                          const opt = fieldOptions.find(o => o.value === value);
                          return opt ? (
                            <span style={{ color: opt.calculated ? 'var(--calculated-color, #d32f2f)' : undefined }}>
                              {opt.label}
                            </span>
                          ) : value;
                        }}
                      >
                        {fieldOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <span style={{ color: opt.calculated ? '#d32f2f' : undefined }}>
                              {opt.label}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              },
            }))}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooter
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ pb: 2, pr: 2 }}>
          <Button onClick={handleClose}>Annulla</Button>
          <Button sx={{ ml: 2 }} onClick={handleImport} variant="contained" color="primary">
            Importa
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
